import { isReviewReplyText } from './reviewReply';
import type { EpiMessage } from '../types/epi-chat-types';
import type { ReviewItem } from '../types/review-types';

/**
 * The questions and library matches of one assistant message, in the order the
 * agent raised them. Left out, because they keep their own surface:
 *  - concept-group disambiguation questions (the gateway binds their pick by
 *    question id, which one combined reply cannot carry for several),
 *  - reference-pattern checkpoints (their alternatives are the whole choice).
 */
export const getReviewItems = (message: EpiMessage): ReviewItem[] => {
  const items: ReviewItem[] = [];
  for (const part of message.parts) {
    if (part.kind === 'clarifying_question') {
      const isConceptGroupQuestion = part.question.options.some((option) =>
        Boolean(option.conceptGroupId)
      );
      if (!isConceptGroupQuestion) {
        items.push({ kind: 'question', id: part.question.questionId, question: part.question });
      }
    } else if (
      part.kind === 'av_selection_checkpoint' &&
      part.checkpoint.purpose !== 'search_reference_variable'
    ) {
      items.push({ kind: 'match', id: part.checkpoint.checkpointId, checkpoint: part.checkpoint });
    }
  }
  return items;
};

export const isReviewItemAnswered = (item: ReviewItem): boolean =>
  item.kind === 'question' ? item.question.answered : item.checkpoint.answered;

/** The reply text recorded on the item, once answered. */
export const getReviewItemAnswerText = (item: ReviewItem): string | undefined =>
  item.kind === 'question' ? item.question.answerText : item.checkpoint.answerText;

/**
 * The items to review now: those of the transcript's LAST assistant message,
 * unless the researcher has already answered them. Items on an older message
 * were passed over (the researcher typed past them or the agent moved on).
 *
 * One combined reply answers the whole group and is recorded on its FIRST item,
 * so the group counts as answered once that item is.
 */
export const getPendingReviewItems = (messages: ReadonlyArray<EpiMessage>): ReviewItem[] => {
  const last = messages[messages.length - 1];
  if (!last || last.role === 'user') return [];
  const items = getReviewItems(last);
  return items.length > 0 && !isReviewItemAnswered(items[0]) ? items : [];
};

/**
 * Whether the message at `index` is the reply the Review card sent, and the answered
 * group on the message before it already shows that reply as a summary card. Such a
 * bubble repeats the card, so the chat leaves it out; the message itself stays in the
 * conversation. Anything else a researcher sends (typed text, or a reply that carries
 * attachments) is never hidden.
 */
export const isReviewReplyShownAsCard = (
  messages: ReadonlyArray<EpiMessage>,
  index: number
): boolean => {
  const message = messages[index];
  const previous = messages[index - 1];
  if (!message || message.role !== 'user' || !previous || previous.role === 'user') return false;
  const [onlyPart] = message.parts;
  if (message.parts.length !== 1 || onlyPart.kind !== 'text') return false;
  if (!isReviewReplyText(onlyPart.text)) return false;
  const items = getReviewItems(previous);
  return items.length > 0 && isReviewItemAnswered(items[0]);
};
